/** @format */

import { createAsyncThunk } from "@reduxjs/toolkit";
import type { RootState } from "../store/store";
import {
    submitListMyBusinesses,
    submitCreateBusiness,
    submitGetActiveBusiness,
    submitUpdateBusinessSettings,
    submitStartQuickBooksConnect,
    submitDisconnectQuickBooks,
    submitGetQuickBooksStatus,
    submitSyncCustomers,
    submitSyncInvoices,
    submitSyncAll,
    submitListSyncJobs,
    submitListCustomers,
    submitGetCustomer,
    submitListInvoices,
    submitGetInvoice,
    submitGetArAging,
    submitGetDashboardOverview,
    submitGetCollectionsMetrics,
    submitGetRecoveryFunnel,
    submitGetRecoveryHealth,
    submitGetRecoveryTrends,
    submitListCampaigns,
    submitGetCampaign,
    submitCreateCampaign,
    submitUpdateCampaign,
    submitDeleteCampaign,
    submitListTemplates,
    submitGetTemplate,
    submitCreateTemplate,
    submitUpdateTemplate,
    submitDeleteTemplate,
    submitListDunningEvents,
    submitRunDunningNow,
    submitSendManualReminder,
    submitGetInvoiceTimeline,
    submitGetRecoveryStateMetadata,
    submitTransitionInvoiceState,
    submitListInvoiceTransitions,
    submitCreatePromise,
    submitListPromisesForInvoice,
    submitListPromisesForBusiness,
    submitMarkPromiseKept,
    submitMarkPromiseBroken,
    submitCancelPromise,
    submitListRecoveryQueue,
    submitGetRecoveryQueueSummary,
    submitAssignInvoice,
    submitManualEscalateInvoice,
    submitListReplies,
    submitGetRepliesSummary,
    submitGetReply,
    submitReprocessReply,
    submitResolveReply,
    submitManualIngestReply,
} from "../services/collectService";
import {
    ICreateBusinessPayload,
    IUpdateBusinessSettingsPayload,
    IUpsertCampaignPayload,
    IUpsertTemplatePayload,
    ISendManualReminderPayload,
    IListInvoicesQuery,
    IListCustomersQuery,
    IListTemplatesQuery,
    IListDunningEventsQuery,
    ITransitionInvoiceStatePayload,
    ICreatePromisePayload,
    IResolvePromisePayload,
    IListPromisesQuery,
    IRecoveryQueueQuery,
    IAssignInvoicePayload,
    IListRepliesQuery,
    IResolveReplyPayload,
    IManualIngestReplyPayload,
    CollectionsRange,
} from "../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// Most Collect thunks need the active businessId. We resolve it from
// state.collect.activeBusinessId when callers don't provide one, so UI
// code can dispatch without threading the id through every call site.
// ─────────────────────────────────────────────────────────────────────────
function resolveBusinessId(getState: () => unknown, override?: string): string {
    if (override) return override;
    const state = getState() as RootState;
    const id = state.collect?.activeBusinessId;
    if (!id) {
        throw new Error("No active business selected.");
    }
    return id;
}

// ── Businesses ──────────────────────────────────────────────────────────
export const fetchMyBusinesses = createAsyncThunk(
    "collect/fetchMyBusinesses",
    async (_: void, { rejectWithValue }) => {
        try {
            return await submitListMyBusinesses();
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load businesses.");
        }
    },
);

export const createBusiness = createAsyncThunk(
    "collect/createBusiness",
    async (payload: ICreateBusinessPayload, { rejectWithValue }) => {
        try {
            return await submitCreateBusiness(payload);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to create business.");
        }
    },
);

export const fetchActiveBusiness = createAsyncThunk(
    "collect/fetchActiveBusiness",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitGetActiveBusiness(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load business.");
        }
    },
);

export const updateBusinessSettings = createAsyncThunk(
    "collect/updateBusinessSettings",
    async (
        payload: IUpdateBusinessSettingsPayload & { businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitUpdateBusinessSettings(id, body);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to update business.");
        }
    },
);

// ── QuickBooks ──────────────────────────────────────────────────────────
export const startQuickBooksConnect = createAsyncThunk(
    "collect/qbo/startConnect",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitStartQuickBooksConnect(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to start QuickBooks.");
        }
    },
);

export const disconnectQuickBooks = createAsyncThunk(
    "collect/qbo/disconnect",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitDisconnectQuickBooks(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to disconnect QuickBooks.");
        }
    },
);

export const fetchQuickBooksStatus = createAsyncThunk(
    "collect/qbo/status",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitGetQuickBooksStatus(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load QuickBooks status.");
        }
    },
);

// ── Sync ────────────────────────────────────────────────────────────────
export const syncCustomers = createAsyncThunk(
    "collect/sync/customers",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitSyncCustomers(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to sync customers.");
        }
    },
);

export const syncInvoices = createAsyncThunk(
    "collect/sync/invoices",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitSyncInvoices(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to sync invoices.");
        }
    },
);

export const syncAll = createAsyncThunk(
    "collect/sync/all",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitSyncAll(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to sync from QuickBooks.");
        }
    },
);

export const fetchSyncJobs = createAsyncThunk(
    "collect/sync/jobs",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitListSyncJobs(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load sync history.");
        }
    },
);

// ── Customers ───────────────────────────────────────────────────────────
export const fetchCustomers = createAsyncThunk(
    "collect/fetchCustomers",
    async (
        payload: IListCustomersQuery & { businessId?: string } = {},
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, ...query } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitListCustomers(id, query);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load customers.");
        }
    },
);

export const fetchCustomer = createAsyncThunk(
    "collect/fetchCustomer",
    async (
        payload: { customerId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitGetCustomer(id, payload.customerId);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load customer.");
        }
    },
);

// ── Invoices ────────────────────────────────────────────────────────────
export const fetchInvoices = createAsyncThunk(
    "collect/fetchInvoices",
    async (
        payload: IListInvoicesQuery & { businessId?: string } = {},
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, ...query } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitListInvoices(id, query);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load invoices.");
        }
    },
);

export const fetchInvoice = createAsyncThunk(
    "collect/fetchInvoice",
    async (
        payload: { invoiceId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitGetInvoice(id, payload.invoiceId);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load invoice.");
        }
    },
);

export const fetchArAging = createAsyncThunk(
    "collect/fetchArAging",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitGetArAging(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load AR aging.");
        }
    },
);

export const fetchDashboardOverview = createAsyncThunk(
    "collect/fetchDashboardOverview",
    async (
        payload: { businessId?: string; limit?: number } | undefined,
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload?.businessId);
            return await submitGetDashboardOverview(id, payload?.limit ?? 8);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to load dashboard overview.",
            );
        }
    },
);

export const fetchCollectionsMetrics = createAsyncThunk(
    "collect/fetchCollectionsMetrics",
    async (
        payload: { businessId?: string; range?: CollectionsRange } | undefined,
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload?.businessId);
            return await submitGetCollectionsMetrics(id, payload?.range ?? "4w");
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to load collections metrics.",
            );
        }
    },
);

export const fetchRecoveryFunnel = createAsyncThunk(
    "collect/fetchRecoveryFunnel",
    async (
        payload: { businessId?: string } | undefined,
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload?.businessId);
            return await submitGetRecoveryFunnel(id);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to load recovery funnel.",
            );
        }
    },
);

export const fetchRecoveryHealth = createAsyncThunk(
    "collect/fetchRecoveryHealth",
    async (
        payload: { businessId?: string } | undefined,
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload?.businessId);
            return await submitGetRecoveryHealth(id);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to load recovery health.",
            );
        }
    },
);

export const fetchRecoveryTrends = createAsyncThunk(
    "collect/fetchRecoveryTrends",
    async (
        payload: { businessId?: string; days?: number } | undefined,
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload?.businessId);
            return await submitGetRecoveryTrends(id, payload?.days ?? 30);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to load recovery trends.",
            );
        }
    },
);

// ── Campaigns ───────────────────────────────────────────────────────────
export const fetchCampaigns = createAsyncThunk(
    "collect/fetchCampaigns",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitListCampaigns(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load campaigns.");
        }
    },
);

export const fetchCampaign = createAsyncThunk(
    "collect/fetchCampaign",
    async (
        payload: { campaignId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitGetCampaign(id, payload.campaignId);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load campaign.");
        }
    },
);

export const createCampaign = createAsyncThunk(
    "collect/createCampaign",
    async (
        payload: IUpsertCampaignPayload & { businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitCreateCampaign(id, body);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to create campaign.");
        }
    },
);

export const updateCampaign = createAsyncThunk(
    "collect/updateCampaign",
    async (
        payload: { campaignId: string; businessId?: string } & IUpsertCampaignPayload,
        { getState, rejectWithValue },
    ) => {
        try {
            const { campaignId, businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitUpdateCampaign(id, campaignId, body);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to update campaign.");
        }
    },
);

export const deleteCampaign = createAsyncThunk(
    "collect/deleteCampaign",
    async (
        payload: { campaignId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            await submitDeleteCampaign(id, payload.campaignId);
            return payload.campaignId;
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to delete campaign.");
        }
    },
);

// ── Templates ───────────────────────────────────────────────────────────
export const fetchTemplates = createAsyncThunk(
    "collect/fetchTemplates",
    async (
        payload: IListTemplatesQuery & { businessId?: string } = {},
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, ...query } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitListTemplates(id, query);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load templates.");
        }
    },
);

export const fetchTemplate = createAsyncThunk(
    "collect/fetchTemplate",
    async (
        payload: { templateId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitGetTemplate(id, payload.templateId);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load template.");
        }
    },
);

export const createTemplate = createAsyncThunk(
    "collect/createTemplate",
    async (
        payload: IUpsertTemplatePayload & { businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitCreateTemplate(id, body);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to create template.");
        }
    },
);

export const updateTemplate = createAsyncThunk(
    "collect/updateTemplate",
    async (
        payload: { templateId: string; businessId?: string } & IUpsertTemplatePayload,
        { getState, rejectWithValue },
    ) => {
        try {
            const { templateId, businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitUpdateTemplate(id, templateId, body);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to update template.");
        }
    },
);

export const deleteTemplate = createAsyncThunk(
    "collect/deleteTemplate",
    async (
        payload: { templateId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            await submitDeleteTemplate(id, payload.templateId);
            return payload.templateId;
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to delete template.");
        }
    },
);

// ── Dunning activity + manual actions ───────────────────────────────────
export const fetchDunningEvents = createAsyncThunk(
    "collect/fetchDunningEvents",
    async (
        payload: IListDunningEventsQuery & { businessId?: string } = {},
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, ...query } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitListDunningEvents(id, query);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load dunning activity.");
        }
    },
);

export const runDunningNow = createAsyncThunk(
    "collect/runDunningNow",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitRunDunningNow(id);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to run dunning.");
        }
    },
);

export const sendManualReminder = createAsyncThunk(
    "collect/sendManualReminder",
    async (
        payload: { invoiceId: string; businessId?: string } & ISendManualReminderPayload,
        { getState, rejectWithValue },
    ) => {
        try {
            const { invoiceId, businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitSendManualReminder(id, invoiceId, body);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to send reminder.");
        }
    },
);

export const fetchInvoiceTimeline = createAsyncThunk(
    "collect/fetchInvoiceTimeline",
    async (
        payload: { invoiceId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitGetInvoiceTimeline(id, payload.invoiceId);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load invoice timeline.");
        }
    },
);

// ── Recovery state machine (Stage 2A) ───────────────────────────────────
export const fetchRecoveryStateMetadata = createAsyncThunk(
    "collect/fetchRecoveryStateMetadata",
    async (businessId: string | undefined, { getState, rejectWithValue }) => {
        try {
            const id = resolveBusinessId(getState, businessId);
            return await submitGetRecoveryStateMetadata(id);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to load recovery state metadata.",
            );
        }
    },
);

export const transitionInvoiceState = createAsyncThunk(
    "collect/transitionInvoiceState",
    async (
        payload: {
            invoiceId: string;
            businessId?: string;
        } & ITransitionInvoiceStatePayload,
        { getState, dispatch, rejectWithValue },
    ) => {
        try {
            const { invoiceId, businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            const result = await submitTransitionInvoiceState(id, invoiceId, body);
            dispatch(fetchDashboardOverview({ limit: 12 }));
            return result;
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to change invoice state.",
            );
        }
    },
);

export const fetchInvoiceTransitions = createAsyncThunk(
    "collect/fetchInvoiceTransitions",
    async (
        payload: { invoiceId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitListInvoiceTransitions(id, payload.invoiceId);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to load invoice state history.",
            );
        }
    },
);

// ── Promise-to-Pay (Stage 2B) ───────────────────────────────────────────
export const createInvoicePromise = createAsyncThunk(
    "collect/createInvoicePromise",
    async (
        payload: { invoiceId: string; businessId?: string } & ICreatePromisePayload,
        { getState, dispatch, rejectWithValue },
    ) => {
        try {
            const { invoiceId, businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            const result = await submitCreatePromise(id, invoiceId, body);
            dispatch(fetchDashboardOverview({ limit: 12 }));
            return result;
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to create promise.");
        }
    },
);

export const fetchInvoicePromises = createAsyncThunk(
    "collect/fetchInvoicePromises",
    async (
        payload: { invoiceId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitListPromisesForInvoice(id, payload.invoiceId);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load promises.");
        }
    },
);

export const fetchBusinessPromises = createAsyncThunk(
    "collect/fetchBusinessPromises",
    async (
        payload: { businessId?: string } & IListPromisesQuery = {},
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, ...query } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitListPromisesForBusiness(id, query);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load promises.");
        }
    },
);

export const markPromiseKept = createAsyncThunk(
    "collect/markPromiseKept",
    async (
        payload: { promiseId: string; businessId?: string } & IResolvePromisePayload,
        { getState, dispatch, rejectWithValue },
    ) => {
        try {
            const { promiseId, businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            const result = await submitMarkPromiseKept(id, promiseId, body);
            dispatch(fetchDashboardOverview({ limit: 12 }));
            return result;
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to mark promise kept.");
        }
    },
);

export const markPromiseBroken = createAsyncThunk(
    "collect/markPromiseBroken",
    async (
        payload: { promiseId: string; businessId?: string } & IResolvePromisePayload,
        { getState, dispatch, rejectWithValue },
    ) => {
        try {
            const { promiseId, businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            const result = await submitMarkPromiseBroken(id, promiseId, body);
            dispatch(fetchDashboardOverview({ limit: 12 }));
            return result;
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to mark promise broken.",
            );
        }
    },
);

export const cancelInvoicePromise = createAsyncThunk(
    "collect/cancelInvoicePromise",
    async (
        payload: { promiseId: string; businessId?: string } & IResolvePromisePayload,
        { getState, dispatch, rejectWithValue },
    ) => {
        try {
            const { promiseId, businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            const result = await submitCancelPromise(id, promiseId, body);
            dispatch(fetchDashboardOverview({ limit: 12 }));
            return result;
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to cancel promise.");
        }
    },
);

// ── Recovery Queue + Escalation (Stage 2C) ─────────────────────
export const fetchRecoveryQueue = createAsyncThunk(
    "collect/fetchRecoveryQueue",
    async (
        payload: { businessId?: string } & IRecoveryQueueQuery = {},
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, ...query } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitListRecoveryQueue(id, query);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to load recovery queue.",
            );
        }
    },
);

export const fetchRecoveryQueueSummary = createAsyncThunk(
    "collect/fetchRecoveryQueueSummary",
    async (
        payload: { businessId?: string } = {},
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitGetRecoveryQueueSummary(id);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to load recovery queue summary.",
            );
        }
    },
);

export const assignInvoice = createAsyncThunk(
    "collect/assignInvoice",
    async (
        payload: { invoiceId: string; businessId?: string } & IAssignInvoicePayload,
        { getState, dispatch, rejectWithValue },
    ) => {
        try {
            const { invoiceId, businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            const result = await submitAssignInvoice(id, invoiceId, body);
            dispatch(fetchDashboardOverview({ limit: 12 }));
            return result;
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to assign invoice.");
        }
    },
);

export const manualEscalateInvoice = createAsyncThunk(
    "collect/manualEscalateInvoice",
    async (
        payload: { invoiceId: string; businessId?: string },
        { getState, dispatch, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            const result = await submitManualEscalateInvoice(
                id,
                payload.invoiceId,
            );
            dispatch(fetchDashboardOverview({ limit: 12 }));
            return result;
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to escalate invoice.",
            );
        }
    },
);

// ── Customer Replies (Stage 2D) ─────────────────────
export const fetchReplies = createAsyncThunk(
    "collect/fetchReplies",
    async (
        payload: { businessId?: string; append?: boolean } & IListRepliesQuery = {},
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, append, ...query } = payload;
            const id = resolveBusinessId(getState, businessId);
            const page = await submitListReplies(id, query);
            return { page, append: !!append };
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load replies.");
        }
    },
);

export const fetchRepliesSummary = createAsyncThunk(
    "collect/fetchRepliesSummary",
    async (
        payload: { businessId?: string } = {},
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitGetRepliesSummary(id);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to load replies summary.",
            );
        }
    },
);

export const fetchReply = createAsyncThunk(
    "collect/fetchReply",
    async (
        payload: { replyId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitGetReply(id, payload.replyId);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to load reply.");
        }
    },
);

export const reprocessReply = createAsyncThunk(
    "collect/reprocessReply",
    async (
        payload: { replyId: string; businessId?: string },
        { getState, rejectWithValue },
    ) => {
        try {
            const id = resolveBusinessId(getState, payload.businessId);
            return await submitReprocessReply(id, payload.replyId);
        } catch (e: any) {
            return rejectWithValue(
                e.message || "Failed to reprocess reply.",
            );
        }
    },
);

export const resolveReply = createAsyncThunk(
    "collect/resolveReply",
    async (
        payload: { replyId: string; businessId?: string } & IResolveReplyPayload,
        { getState, rejectWithValue },
    ) => {
        try {
            const { replyId, businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitResolveReply(id, replyId, body);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to resolve reply.");
        }
    },
);

export const ingestManualReply = createAsyncThunk(
    "collect/ingestManualReply",
    async (
        payload: { businessId?: string } & IManualIngestReplyPayload,
        { getState, rejectWithValue },
    ) => {
        try {
            const { businessId, ...body } = payload;
            const id = resolveBusinessId(getState, businessId);
            return await submitManualIngestReply(id, body);
        } catch (e: any) {
            return rejectWithValue(e.message || "Failed to ingest reply.");
        }
    },
);
